import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearDroneData,
  clearMissionResult,
  latchMissionResult,
  setConnected,
  setControl,
  setMissionProgress,
  setMissionReturning,
  setSelectedDrone,
  setUploadStatus,
  updateDroneData,
} from "../features/DroneSlice";
import {
  setRouteConfirmed,
  setWaypoints,
} from "../features/RouteSlice";
import { setDiagnosisUpdate } from "../features/FarmSlice";
import axios from "../lib/http/axiosConfig";
import {
  markDroneTelemetry,
  resetDroneTelemetryClock,
} from "../lib/droneTelemetryClock";
import {
  ensureFreshAccessToken,
  forceRefreshAccessToken,
} from "../lib/http/axiosConfig";
import stomp from "../lib/realtime/stompClient";
import { ensureStompConnected } from "../lib/realtime/ensureStompConnected";
import {
  clearMissionEndLatches,
  didMissionStop,
  dismissMissionStopReturningToast,
  isMissionStopRequestedLatched,
  markMissionCompleted,
  showMissionEndToastIfNeeded,
  showMissionReturningToast,
  showMissionStopReadyToast,
} from "../utils/missionStoppedToast";
import { useDemoScenario } from "../demo/DemoScenarioContext";

// 착륙으로 미션이 끝난 뒤 완주 결과(진행 링 N/N)를 얼마나 더 보여 줄지.
// N/N 은 마지막 구역을 찍은 순간부터 복귀 내내 이미 보였으므로 짧아도 된다.
// 착륙 토스트와 숫자가 같은 순간에 바뀌지 않을 만큼만 둔다.
const MISSION_RESULT_HOLD_MS = 2_000;
// [복귀 잠금 안전장치] 복귀 잠금(missionReturning)은 드론의 실제 착륙(arming false 전이)으로 푼다.
// 착륙 프레임을 통째로 놓친 경우에만 이 시간 뒤 강제로 푼다. 완주·수동 Stop 둘 다 같은 값.
const RETURNING_SAFETY_MS = 60_000;

export function useDroneSocket() {
  const dispatch = useDispatch();
  const { isDemo } = useDemoScenario();
  const droneId = useSelector((state) => state.drone.selectedDrone);
  const farmId = useSelector((state) => state.farm.farmId);
  const control = useSelector((state) => state.drone.control);
  const droneData = useSelector((state) => state.drone.droneData);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  const controlRef = useRef(control);
  const droneDataRef = useRef(droneData);
  const missionReturningRef = useRef(missionReturning);
  const retryCountRef = useRef(0);
  const refreshAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const isConnectingRef = useRef(false);
  const stopRef = useRef(false);
  const returningRef = useRef(false);
  // 착륙 뒤 완주 결과(N/N)를 잠깐 보여 준 다음 내리는 타이머.
  const resultClearTimerRef = useRef(null);

  useEffect(() => {
    controlRef.current = control;
  }, [control]);

  useEffect(() => {
    missionReturningRef.current = missionReturning;
  }, [missionReturning]);

  // 복귀 잠금이 켜지면 안전장치 타이머를 걸고, 꺼지면(착륙 이벤트로 정상 해제) 타이머를 지운다.
  // 예전엔 완주 경로는 여기 60s, 수동 Stop 경로는 DroneControl 의 고정 10s 타이머로 풀어서,
  // 실기체 복귀가 10s 를 넘기면 비행 중에 조작이 풀렸다. 데모는 시나리오가 직접 켜고 끈다.
  useEffect(() => {
    if (isDemo || !missionReturning) return undefined;
    const timer = setTimeout(() => {
      returningRef.current = false;
      dispatch(setMissionReturning(false));
      if (isMissionStopRequestedLatched()) {
        dismissMissionStopReturningToast();
        showMissionStopReadyToast();
      }
    }, RETURNING_SAFETY_MS);
    return () => clearTimeout(timer);
  }, [isDemo, missionReturning, dispatch]);

  useEffect(() => {
    droneDataRef.current = droneData;
  }, [droneData]);

  useEffect(() => {
    if (isDemo) return undefined;
    let cancelled = false;

    const ensureSelectedDrone = async () => {
      if (!farmId) {
        dispatch(setSelectedDrone(null));
        return;
      }

      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/info`);
        const nextId = data?.data?.droneInfo?.id ?? null;
        if (cancelled) return;
        if (nextId !== droneId) dispatch(setSelectedDrone(nextId));
      } catch (error) {
        console.warn("[STOMP] failed to resolve drone id", error);
      }
    };

    ensureSelectedDrone();

    return () => {
      cancelled = true;
    };
  }, [farmId, droneId, dispatch, isDemo]);

  useEffect(() => {
    if (isDemo) return undefined;
    if (!droneId) return;

    let isMounted = true;
    stopRef.current = false;
    retryCountRef.current = 0;
    refreshAttemptRef.current = 0;
    let sub = null;
    let offClose = null;
    let offError = null;

    const toNum = (value) => {
      if (value === null || value === undefined || value === "") return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };

    const readNum = (source, keys) => {
      if (!source || typeof source !== "object") return null;
      for (const key of keys) {
        const num = toNum(source[key]);
        if (num !== null) return num;
      }
      return null;
    };

    const subscribeDrone = () => {
      sub?.unsubscribe?.();
      sub = stomp.subscribe(`/sub/drone-info/${droneId}`, (frame) => {
        try {
          const payload = JSON.parse(frame.body);
          const { type, data } = payload || {};
          if (!data) return;

          // AI 병해 결과가 BE 에 저장된 직후 배치당 1건. 지도(DroneMapTest)가 이 값을 구독해
          // 진단 오버레이와 회차 목록을 다시 조회한다. 여기서는 알리기만 한다(훅은 지도를 모른다).
          // 드론 텔레메트리가 아니므로 markDroneTelemetry 앞에서 끊는다. 결과는 착륙 몇 분 뒤에
          // 오기 때문에, 이걸 텔레메트리로 치면 꺼진 드론이 살아 있는 것으로 보인다.
          if (type === "DIAGNOSIS_UPDATED") {
            dispatch(
              setDiagnosisUpdate({
                farmId: data.farmId ?? data.farm_id ?? null,
                missionLogId: data.missionLogId ?? data.mission_log_id ?? null,
                cellIds: Array.isArray(data.gridCellIds)
                  ? data.gridCellIds
                  : Array.isArray(data.grid_cell_ids)
                  ? data.grid_cell_ids
                  : [],
                at: Date.now(),
              })
            );
            return;
          }

          markDroneTelemetry();
          // [mission_progress] 드론팀 신규 규격 — event 대신 단계 문자열이 온다.
          // complete 는 완료 래치(completed/stopped 분류)에 연결하고, 단계 자체는 라벨 표시용으로 저장한다.
          const progressRaw =
            typeof (data.mission_progress ?? data.missionProgress) === "string"
              ? (data.mission_progress ?? data.missionProgress)
                  .trim()
                  .toLowerCase()
              : null;
          if (progressRaw !== null) {
            dispatch(setMissionProgress(progressRaw));
          }
          const progressReturning =
            progressRaw === "returning" || progressRaw === "landing";
          // 완료 판정은 mission_progress 만 (2026-09-02). 구 event: mission_complete 는
          // 다음 미션 비행 중 뒤늦게 도착해 복귀 오버레이 오작동·로그 조기 완료를 일으켜 제거.
          const isMissionComplete = progressRaw === "complete";

          const patch = {};
          const hasOwn = (obj, key) =>
            Boolean(obj && typeof obj === "object" &&
              Object.prototype.hasOwnProperty.call(obj, key));
          const assignNum = (key, value) => {
            const num = toNum(value);
            if (num !== null) patch[key] = num;
          };
          const assignNumFromKeys = (key, source, keys) => {
            const num = readNum(source, keys);
            if (num !== null) patch[key] = num;
          };

          if (type === "FULL_STATUS") {
            patch.serial_num = data.serial_num ?? data.serialNum ?? null;
            patch.drone_id = data.drone_id ?? data.droneId ?? null;
            patch.farm_id = data.farm_id ?? data.farmId ?? null;
            patch.route_id = data.route_id ?? data.routeId ?? null;
            patch.mode = data.mode ?? null;
            patch.power_status =
              typeof (data.power_status ?? data.powerStatus) === "boolean"
                ? data.power_status ?? data.powerStatus
                : null;
            patch.arming_status =
              typeof (data.arming_status ?? data.armingStatus) === "boolean"
                ? data.arming_status ?? data.armingStatus
                : null;
            patch.mission_status =
              typeof (data.mission_status ?? data.missionStatus) === "boolean"
                ? data.mission_status ?? data.missionStatus
                : null;
            patch.last_received_message =
              data.last_received_message ?? data.lastReceivedMessage ?? null;
            patch.last_received_at =
              data.last_received_at ?? data.lastReceivedAt ?? null;

            assignNumFromKeys("latitude", data, ["latitude"]);
            assignNumFromKeys("longitude", data, ["longitude"]);
            assignNumFromKeys("altitude", data, ["altitude"]);
            assignNumFromKeys("heading", data, ["heading"]);
            assignNumFromKeys("airspeed", data, ["airspeed"]);
            assignNumFromKeys("groundspeed", data, ["groundspeed"]);
            assignNumFromKeys("battery_voltage", data, [
              "battery_voltage",
              "batteryVoltage",
              "voltageV",
              "voltage",
            ]);
            assignNumFromKeys("battery_current", data, [
              "battery_current",
              "batteryCurrent",
              "current",
            ]);
            assignNumFromKeys("battery_charge", data, [
              "battery_charge",
              "batteryCharge",
              "charge",
              "percent",
            ]);
          } else if (type === "LOCATION") {
            assignNumFromKeys("latitude", data, ["latitude"]);
            assignNumFromKeys("longitude", data, ["longitude"]);
            assignNumFromKeys("altitude", data, ["altitude"]);
            assignNumFromKeys("heading", data, ["heading"]);
            assignNumFromKeys("airspeed", data, ["airspeed"]);
            assignNumFromKeys("groundspeed", data, ["groundspeed"]);
            patch.last_received_at =
              data.last_received_at ?? data.lastReceivedAt ?? null;
          } else if (type === "BATTERY") {
            if (data && typeof data === "object") {
              assignNumFromKeys("battery_charge", data, [
                "battery_charge",
                "batteryCharge",
                "charge",
                "percent",
                "value",
              ]);
              assignNumFromKeys("battery_voltage", data, [
                "battery_voltage",
                "batteryVoltage",
                "voltageV",
                "voltage",
              ]);
              assignNumFromKeys("battery_current", data, [
                "battery_current",
                "batteryCurrent",
                "current",
              ]);
            } else {
              assignNum("battery_charge", data);
            }
          } else if (type === "ARMED_STATUS") {
            patch.arming_status =
              typeof data === "boolean" ? data : patch.arming_status ?? null;
          } else if (type === "UPLOAD_STATUS") {
            // 드론의 upload_status 보고(값 변화 또는 업로드 직후 확인). FULL_STATUS 프레임의
            // upload_status 는 여기서 반영하지 않는다 — 사용자가 Cancel 로 무효화한 상태가
            // 매 프레임 되살아나지 않게 하기 위함. BE 가 변화/확인 시점에만 이 타입을 보낸다.
            if (data && typeof data.uploadStatus === "boolean") {
              dispatch(
                setUploadStatus({
                  status: data.uploadStatus,
                  routeId: data.routeId ?? null,
                  // 업로드를 접수한 시각. 누르지 않은 PC 도 이 값으로 같은 대기 표시를 낸다.
                  pendingAt: data.pendingAt ?? null,
                })
              );
            }
          } else if (type === "FLIGHT_METRICS") {
            // [1초 틱 기준점] 비행/남은 시간은 이 시각 + 경과초로 화면에서 이어 센다.
            patch.flight_metrics_at = Date.now();
            patch.drone_id = data.droneId ?? patch.drone_id ?? null;
            patch.route_id = data.routeId ?? patch.route_id ?? null;
            assignNum("waypoint_done", data.waypointDone ?? data.waypoint_done);
            assignNum("waypoint_total", data.waypointTotal ?? data.waypoint_total);
            assignNum(
              "waypoint_percent",
              data.waypointPercent ?? data.waypoint_percent
            );
            patch.takeoff_at = data.takeoffAt ?? data.takeoff_at ?? null;
            assignNum("flight_time_sec", data.flightTimeSec ?? data.flight_time_sec);
            assignNum("time_left_sec", data.timeLeftSec ?? data.time_left_sec);
            patch.eta = data.eta ?? null;
          }

          if (hasOwn(data, "streaming")) {
            patch.streaming = data.streaming ?? null;
          }

          if (Object.keys(patch).length) {
            dispatch(updateDroneData(patch));
          }

          const prev = controlRef.current ?? {};

          // 미션 끝 = 착륙(disarm). complete 는 래치만 기억하고 상태는 건드리지 않는다.
          // 최종 분류는 true→false 전환 시점에 showMissionEndToastIfNeeded가 수행.
          // [2026-09-02] percentEdge(진행률 100% = 복귀 넘겨짚기) 제거 — 100%는 "마지막 셀 도착"
          // 일 뿐이고(촬영·복귀 전), 복귀는 mission_progress: returning/landing 이 직접 알려준다.
          if (isMissionComplete) {
            markMissionCompleted();
            // 완주 결과를 래치해 착륙 후에도 진행 링이 N/N 을 유지하게 한다(드론이 10초 뒤 idle 로 돌려도 남는다).
            dispatch(latchMissionResult());
          }
          if (
            (isMissionComplete || progressReturning) &&
            prev.missionStatus === true &&
            patch.mission_status !== false &&
            !returningRef.current
          ) {
            returningRef.current = true;
            dispatch(setMissionReturning(true));
            showMissionReturningToast();
            // disarm 프레임 유실 대비 안전장치는 위의 missionReturning 효과가 건다.
          }

          const nextControlPatch = {};

          if (typeof patch.arming_status === "boolean") {
            if (prev.armingStatus !== patch.arming_status) {
              nextControlPatch.armingStatus = patch.arming_status;
            }
            // [복귀 잠금 해제 = 실제 착륙] armed → disarmed 전이는 드론이 실제로 내려앉아야만 온다
            // (BE handleDisarmTransition 이 ARMED_STATUS false 와 FULL 프레임으로 전파).
            // 수동 Stop 은 BE 가 복귀 중에도 mission_status=false 를 내려보내므로 그걸로는 착륙을
            // 알 수 없어 예전엔 고정 10s 타이머로 풀었다. 이제 이 전이에서 푼다.
            if (
              prev.armingStatus === true &&
              patch.arming_status === false &&
              missionReturningRef.current
            ) {
              returningRef.current = false;
              missionReturningRef.current = false;
              dispatch(setMissionReturning(false));
              if (isMissionStopRequestedLatched()) {
                dismissMissionStopReturningToast();
                showMissionStopReadyToast();
              }
            }
          }
          if (typeof patch.mission_status === "boolean") {
            const manualStopPending = isMissionStopRequestedLatched();
            // 미션이 끝났거나(false 프레임) 막 시작됐으면 복귀 잠금은 유효하지
            // 않다. REST 경로가 종료 전환을 먼저 소비해도 잠금이 남지 않도록
            // 전환 관찰 여부와 무관하게 정리한다(redux는 동일 값이면 no-op).
            if (
              !manualStopPending &&
              (patch.mission_status === false || prev.missionStatus === false)
            ) {
              returningRef.current = false;
              dispatch(setMissionReturning(false));
            }
            // [stale-stop echo 가드] 사용자가 stop을 눌러 수동정지 래치가 활성인 동안
            // 들어온 mission_status=true는 (BE pre-commit 레이스/지연 telemetry의) stale
            // 에코이므로 무시한다. redux를 true로 되돌리지 않아(= 웨이포인트 깜박임 방지),
            // showMissionEndToastIfNeeded(false,true)도 호출하지 않아 래치가 지워지지
            // 않게 한다(= 잘못된 "Mission has been stopped." 방지).
            const isStaleStopEcho =
              manualStopPending &&
              patch.mission_status === true &&
              prev.missionStatus === false;
            if (
              prev.missionStatus !== patch.mission_status &&
              !isStaleStopEcho
            ) {
              nextControlPatch.missionStatus = patch.mission_status;
              showMissionEndToastIfNeeded(
                prev.missionStatus,
                patch.mission_status,
                { progress: progressRaw }
              );
              if (didMissionStop(prev.missionStatus, patch.mission_status)) {
                dispatch(setWaypoints([]));
                // 선택은 서버가 미션 종료를 확정할 때 한 번만 비운다. 화면마다 보내면 늦게 도착한 지우기가 새 선택을 지운다.
                dispatch(setRouteConfirmed(false));
                // 완주 결과(N/N)를 잠깐 더 보여 준 뒤 내린다. 예전엔 다음 업로드를 누를 때까지
                // 지난 비행 숫자가 남아, 착륙한 뒤에도 카드가 비행 중처럼 보였다.
                // 셀 선택은 서버가 비우므로 여기서는 건드리지 않는다. 그래야 링이 0/0 이 된다.
                if (resultClearTimerRef.current) {
                  clearTimeout(resultClearTimerRef.current);
                }
                resultClearTimerRef.current = setTimeout(() => {
                  resultClearTimerRef.current = null;
                  // dismiss: 착륙 후 10초쯤 이어지는 complete 보고가 되살리지 못하게 잠근다.
                  dispatch(clearMissionResult({ dismiss: true }));
                }, MISSION_RESULT_HOLD_MS);
              }
              // 새 미션이 시작되면 지난 결과를 지울 이유가 없다. 예약된 정리를 취소한다.
              if (patch.mission_status === true && resultClearTimerRef.current) {
                clearTimeout(resultClearTimerRef.current);
                resultClearTimerRef.current = null;
              }
            }
          }

          if (Object.keys(nextControlPatch).length) {
            // console.log(
            //   "🔵 [WS] setControl",
            //   "type=", type,
            //   "변경=", nextControlPatch,
            //   "(boolean 값이 true↔false로 튀면 데이터 진동)"
            // );
            const nextControl = { ...prev, ...nextControlPatch };
            dispatch(setControl(nextControl));
            // 같은 React flush 안에서 연속 도착하는 프레임이 stale prev로
            // 같은 전환을 두 번 분류하지 않도록 ref를 즉시 동기화한다
            // (useEffect 동기화는 렌더 이후라 버스트 프레임에 늦는다).
            controlRef.current = nextControl;
          }
        } catch (error) {
          console.error("STOMP payload parse error: ", error);
        }
      });
    };

    const MAX_RETRIES = 6;
    const MAX_REFRESH_ATTEMPTS = 1;
    const REFRESH_EVERY = 3;
    const BASE_DELAY = 1000;
    const MAX_DELAY = 30000;

    const scheduleReconnect = (reason) => {
      if (!isMounted || stopRef.current) return;
      if (reconnectTimerRef.current) return;

      if (retryCountRef.current >= MAX_RETRIES) {
        if (refreshAttemptRef.current >= MAX_REFRESH_ATTEMPTS) {
          console.warn("[STOMP] reconnect stopped after max retries", {
            reason,
          });
          stopRef.current = true;
          return;
        }

        refreshAttemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(async () => {
          reconnectTimerRef.current = null;
          try {
            await forceRefreshAccessToken();
            retryCountRef.current = 0;
          } catch (error) {
            console.warn("[STOMP] token refresh failed after retries", error);
            stopRef.current = true;
            return;
          }

          await connectSocket();
        }, BASE_DELAY);

        return;
      }

      retryCountRef.current += 1;
      const exp = Math.min(
        MAX_DELAY,
        BASE_DELAY * 2 ** Math.max(retryCountRef.current - 1, 0)
      );
      const jitter = Math.random() * 500;
      const delay = exp + jitter;

      reconnectTimerRef.current = setTimeout(async () => {
        reconnectTimerRef.current = null;

        if (retryCountRef.current % REFRESH_EVERY === 0) {
          try {
            await ensureFreshAccessToken();
          } catch (error) {
            console.warn("[STOMP] token refresh failed", error);
          }
        }

        await connectSocket();
      }, delay);
    };

    const connectSocket = async () => {
      if (!isMounted || stopRef.current) return;
      if (isConnectingRef.current) return;
      isConnectingRef.current = true;

      try {
        // 연결 여는 방법은 격자 셀 선택과 공용이다. 두 곳에서 따로 설정하면 한쪽만 고쳐져 어긋난다.
        if (!(await ensureStompConnected())) {
          scheduleReconnect("no-token");
          return;
        }
        if (!isMounted) return;

        retryCountRef.current = 0;
        // console.log("🟢 [STOMP] connected (재연결 루프면 이게 반복 출력됨)");
        dispatch(setConnected(true));
        subscribeDrone();
      } catch (error) {
        console.error("STOMP connect failed:", error);
        scheduleReconnect("connect-failed");
      } finally {
        isConnectingRef.current = false;
      }
    };

    offClose = stomp.onClose(() => {
      dispatch(setConnected(false));
      scheduleReconnect("socket-closed");
    });

    offError = stomp.onError(() => {
      dispatch(setConnected(false));
      scheduleReconnect("socket-error");
    });

    connectSocket();

    return () => {
      isMounted = false;
      stopRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (resultClearTimerRef.current) {
        clearTimeout(resultClearTimerRef.current);
        resultClearTimerRef.current = null;
      }
      // 드론 전환/페이지 이탈 시 이전 드론의 완료 기억이 새 컨텍스트로
      // 새지 않게 래치와 복귀 상태를 함께 리셋한다.
      returningRef.current = false;
      resetDroneTelemetryClock();
      clearMissionEndLatches();
      offClose?.();
      offError?.();
      sub?.unsubscribe();
      stomp.disconnect();
      dispatch(clearDroneData());
    };
  }, [droneId, dispatch, isDemo]);
}
